import { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import {
  Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableHead, TableRow, TableCell, TableBody, Chip, Switch,
  MenuItem, Box, IconButton, Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyIcon from "@mui/icons-material/Key";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import BadgeIcon from "@mui/icons-material/Badge";
import DeleteIcon from "@mui/icons-material/Delete";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";

const EMPTY_NEW = { username: "", nombre: "", email: "", password: "", numero_nomina: "" };
const EMPTY_EDIT = { username: "", nombre: "", email: "", numero_nomina: "" };

export default function AdminUsuarios() {
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);

  const [openNew, setOpenNew] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW);

  const [openEdit, setOpenEdit] = useState(false);
  const [editUser, setEditUser] = useState(EMPTY_EDIT);
  const [selUser, setSelUser] = useState(null);

  const [openRoles, setOpenRoles] = useState(false);
  const [selRole, setSelRole] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(null);

  const cargar = useCallback(async () => {
    const [usersRes, rolesRes] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/roles"),
    ]);
    setData(usersRes.data);
    setRoles(rolesRes.data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  /* ── Create user ── */
  const crear = async () => {
    if (!newUser.username || !newUser.password) return alert("Username and password are required");
    try {
      await api.post("/admin/users", {
        username: newUser.username,
        nombre: newUser.nombre.trim() || null,
        email: newUser.email || null,
        password: newUser.password,
        numero_nomina: newUser.numero_nomina || null,
      });
      setOpenNew(false);
      setNewUser(EMPTY_NEW);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  /* ── Edit user ── */
  const abrirEdit = (u) => {
    setSelUser(u);
    setEditUser({ username: u.username, nombre: u.nombre || "", email: u.email || "", numero_nomina: u.numero_nomina || "" });
    setOpenEdit(true);
  };
  const guardarEdit = async () => {
    if (!editUser.username.trim()) return alert("Username cannot be empty.");
    try {
      await api.patch(`/admin/users/${selUser.id_usuario}`, {
        username: editUser.username.trim(),
        nombre: editUser.nombre.trim() || null,
        email: editUser.email || null,
        numero_nomina: editUser.numero_nomina || null,
      });
      setOpenEdit(false);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  /* ── Delete user ── */
  const eliminar = async () => {
    try {
      await api.delete(`/admin/users/${confirmDelete.id_usuario}`);
      setConfirmDelete(null);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  /* ── Activate / deactivate ── */
  const toggleActivo = async (u) => {
    await api.patch(`/admin/users/${u.id_usuario}`, { activo: !u.activo });
    cargar();
  };

  /* ── Roles ── */
  const abrirRoles = (u) => { setSelUser(u); setOpenRoles(true); setSelRole(""); };
  const asignarRol = async () => {
    if (!selRole) return;
    await api.post(`/admin/users/${selUser.id_usuario}/roles`, { id_rol: parseInt(selRole) });
    cargar();
  };
  const quitarRol = async (idRol) => {
    await api.delete(`/admin/users/${selUser.id_usuario}/roles/${idRol}`);
    cargar();
  };

  /* ── Reset password ── */
  const resetPass = async (u) => {
    const newPass = prompt(`New password for ${u.username}:`);
    if (!newPass) return;
    await api.post(`/admin/users/${u.id_usuario}/reset_password`, { new_password: newPass });
    alert("Password updated");
  };

  /* ── Resend welcome email ── */
  const reenviarBienvenida = async (u) => {
    if (!u.email) return alert("This user has no email address on file.");
    if (!window.confirm(
      `Resend the welcome email to ${u.username} (${u.email})?\n\nThis generates a new password and invalidates the current one.`
    )) return;
    try {
      await api.post(`/admin/users/${u.id_usuario}/resend_welcome_email`);
      alert("Welcome email resent.");
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>User Administration</Typography>

      <Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 2 }} onClick={() => setOpenNew(true)}>
        New User
      </Button>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Username</TableCell>
            <TableCell sx={{ color: "white" }}>Name</TableCell>
            <TableCell sx={{ color: "white" }}>Email</TableCell>
            <TableCell sx={{ color: "white" }}>Payroll # / Access</TableCell>
            <TableCell sx={{ color: "white" }}>Active</TableCell>
            <TableCell sx={{ color: "white" }}>Roles</TableCell>
            <TableCell sx={{ color: "white" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(u => (
            <TableRow key={u.id_usuario} hover>
              <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
              <TableCell>{u.nombre || <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell>
              <TableCell>{u.email || "—"}</TableCell>
              <TableCell>
                {u.numero_nomina
                  ? <Chip
                      icon={<BadgeIcon />}
                      label={`Payroll ${u.numero_nomina}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  : <Chip label="Global access" size="small" variant="outlined" sx={{ color: "text.secondary" }} />
                }
              </TableCell>
              <TableCell>
                <Switch checked={u.activo} onChange={() => toggleActivo(u)} size="small" />
              </TableCell>
              <TableCell>
                {u.roles.length
                  ? u.roles.map((r, i) => <Chip key={i} label={r} size="small" sx={{ mr: 0.5 }} />)
                  : <Typography variant="body2" color="text.secondary">—</Typography>}
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Edit user">
                    <IconButton size="small" color="primary" onClick={() => abrirEdit(u)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Manage roles">
                    <IconButton size="small" onClick={() => abrirRoles(u)}>
                      <GroupIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset password">
                    <IconButton size="small" onClick={() => resetPass(u)}>
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={u.email ? "Resend welcome email" : "No email on file"}>
                    <span>
                      <IconButton size="small" disabled={!u.email} onClick={() => reenviarBienvenida(u)}>
                        <ForwardToInboxIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton size="small" color="error" onClick={() => setConfirmDelete(u)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* ── New User dialog ── */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} fullWidth maxWidth="sm">
        <DialogTitle>New User</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Username *"
            fullWidth
            value={newUser.username}
            onChange={e => setNewUser({ ...newUser, username: e.target.value })}
          />
          <TextField
            label="Full name"
            fullWidth
            value={newUser.nombre}
            onChange={e => setNewUser({ ...newUser, nombre: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            value={newUser.email}
            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
          />
          <TextField
            label="Password *"
            type="password"
            fullWidth
            value={newUser.password}
            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
          />
          <TextField
            label="Payroll number (only if a supervisor)"
            fullWidth
            value={newUser.numero_nomina}
            onChange={e => setNewUser({ ...newUser, numero_nomina: e.target.value })}
            helperText="If assigned and the user has subordinates in the system, they will see only their direct team."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenNew(false); setNewUser(EMPTY_NEW); }}>Cancel</Button>
          <Button variant="contained" onClick={crear}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit User dialog ── */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Username *"
            fullWidth
            value={editUser.username}
            onChange={e => setEditUser({ ...editUser, username: e.target.value })}
          />
          <TextField
            label="Full name"
            fullWidth
            value={editUser.nombre}
            onChange={e => setEditUser({ ...editUser, nombre: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            value={editUser.email}
            onChange={e => setEditUser({ ...editUser, email: e.target.value })}
          />
          <TextField
            label="Payroll number"
            fullWidth
            value={editUser.numero_nomina}
            onChange={e => setEditUser({ ...editUser, numero_nomina: e.target.value })}
            helperText="Leave it empty for global access. If they have subordinates assigned, they will see only their team."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={guardarEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete the user{" "}
            <strong>{confirmDelete?.username}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={eliminar}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Roles dialog ── */}
      <Dialog open={openRoles} onClose={() => setOpenRoles(false)} fullWidth maxWidth="sm">
        <DialogTitle>Roles: {selUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            select label="Add role" fullWidth value={selRole}
            onChange={e => setSelRole(e.target.value)} sx={{ mt: 1 }}
          >
            <MenuItem value="">— Select —</MenuItem>
            {roles.map(r => (
              <MenuItem key={r.id_rol} value={r.id_rol}>{r.nombre_rol}</MenuItem>
            ))}
          </TextField>
          <Typography sx={{ mt: 2, mb: 1 }} variant="body2" color="text.secondary">Current roles:</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selUser?.roles?.length
              ? selUser.roles.map((rn, idx) => {
                  const rolObj = roles.find(r => r.nombre_rol === rn);
                  return (
                    <Chip key={idx} label={rn}
                      onDelete={rolObj ? () => quitarRol(rolObj.id_rol) : undefined}
                    />
                  );
                })
              : <Typography color="text.secondary">No roles assigned</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoles(false)}>Close</Button>
          <Button variant="contained" onClick={asignarRol} disabled={!selRole}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
